import { useDispatch } from 'react-redux';
import { Url } from '@wix/da-url';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks/useFlag';
import { BiData, SubmitButtonClickBiEvent } from '@wix/da-bi/pkg/events';
import { Props as BaseButtonProps } from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink/PlainButtonOrLink';
import { useRestrictToLoggedInAndVerified } from '@wix/da-shared-react/pkg/utils/hooks/useRestrictToLoggedInAndVerified';
import { pushModal } from '@wix/da-shared-react/pkg/Modals/redux/basicActions';
import { ModalType } from '@wix/da-shared-react/pkg/Modals/redux/types';

export type UseSubmitButtonPropsProps = {
  /** deviation tags that will be pre-filled in the submit form */
  tags?: string | string[];
  /** should we use eclipse submit? */
  forceEclipseSubmit?: boolean;
  biSectionname?: string;
};

export type SubmitButtonProps = Pick<
  BaseButtonProps,
  'onClick' | 'href' | 'biData'
>;

/** Return the href, biData, and onClick required for a submit button (verification handled) */
export default function useSubmitButtonProps(
  {
    tags,
    forceEclipseSubmit,
    biSectionname = 'general',
  } = {} as UseSubmitButtonPropsProps
): SubmitButtonProps {
  const eclipse_submit_studio = useFlag('eclipse_submit_studio');
  const eclipseSubmitModalEnabled =
    !eclipse_submit_studio && forceEclipseSubmit && !tags;

  const dispatch = useDispatch();
  const openSubmitModal = () =>
    dispatch(pushModal(ModalType.DEVIATION_SUBMIT, {}));

  const { restrictToLoggedInAndVerified } = useRestrictToLoggedInAndVerified();
  const onClick = eclipseSubmitModalEnabled
    ? restrictToLoggedInAndVerified(
        ReferralType.SUBMIT,
        'submit',
        openSubmitModal
      )
    : undefined;

  let href;

  if (!eclipseSubmitModalEnabled) {
    const url = eclipse_submit_studio
      ? Url.buildFromPath('/studio')
      : forceEclipseSubmit
      ? Url.buildFromPath('/_deviation_submit')
      : Url.buildFromPath('/submit');

    const params = new URLSearchParams();
    if (eclipse_submit_studio) {
      params.append('new', '1');
    }
    if (tags) {
      const tagsAsParam = Array.isArray(tags) ? tags.join(',') : tags;
      params.append('tags', tagsAsParam);
    }

    const qs = params.toString();
    href = `${url}${qs ? `?${qs}` : ''}`;
  }

  const biData = BiData<SubmitButtonClickBiEvent>({
    evid: 704,
    sectionname: biSectionname,
  });

  return {
    onClick,
    href,
    biData,
  };
}
